import { Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NgForm } from '@angular/forms';

import { ApiError } from '../../errors';
import { ButtonLoader } from '../../utils';
import { AuthService, MessageService, Crud, FormsUtilsService } from '../../services';

import { BaseEntity, Representavel} from '../../../models';
import { Representante } from '../../../home/pessoa/representante';

declare let jQuery: any;

/**
 * Service com métodos para os components de formulários de cadastro da aplicação
 */
@Injectable({
    providedIn: 'root'
})
export class BaseFormService {

    constructor(private router: Router,
                private authService: AuthService,
                private messageService: MessageService,
                private formsUtilsService: FormsUtilsService) { }


    /**
     * Pega o id da entidade nos Params da rota
     */
    getIdParam(activatedRoute: ActivatedRoute): Promise<number> {
        return new Promise<number>((resolve, reject) => {
             activatedRoute.params.subscribe((params: Params) => {
                const id = +params['id']; // Convertendo o id para um number
                if (isNaN(id)) {
                    resolve(null);
                } else {
                    resolve(id);
                }
            });
        });
    }


    /**
     * Save padrão das entidades
     *
     * @param entity Entidade a ser salva
     * @param form Formulário do cadastro
     * @param editMode Se está em mode de edição ou não
     * @param serviceHttp Service Http da entidade utilizado para realizar o PUT/POST
     * @param constrolBtnSave Se deve controlar as animações do botão de save do formulário
     */
    save<T extends BaseEntity>(entity: T, form: NgForm, editMode: boolean, serviceHttp: Crud<T>,
                               controlBtnSave = true): Promise<T> {

        return new Promise<T>((resolve, reject) => {
            this.formsUtilsService.makeAllFieldsDirty(form);

            if (!form.invalid) {
                const btnSave = new ButtonLoader('#btnSave');
                if (controlBtnSave) {
                    btnSave.loading('Salvando');
                }

                if (editMode) { // PUT
                    serviceHttp.update(entity)
                        .subscribe(
                            entityResponse => {
                                if (controlBtnSave) {
                                    btnSave.reset();
                                }
                                resolve(entityResponse);
                            },
                            (error: ApiError) => {
                                this.messageService.error(error.message, error.cause, error);
                                if (controlBtnSave) {
                                    btnSave.reset();
                                }
                                reject(error);
                            }
                        );
                } else { // POST
                    serviceHttp.insert(entity)
                        .subscribe(
                            entityResponse => {
                                if (controlBtnSave) {
                                    btnSave.reset();
                                }
                                resolve(entityResponse);
                            },
                            (error: ApiError) => {
                                this.messageService.error(error.message, error.cause, error);
                                if (controlBtnSave) {
                                    btnSave.reset();
                                }
                                reject(error);
                            }
                        );
                }
            } else {
                this.formsUtilsService.showMsgFormInvalid();
                reject(null);
            }
        });
    }

    /**
     * Realiza o cancel do cadastro e volta para a visualização
     */
    cancel(activatedRoute: ActivatedRoute) {
        this.router.navigate(['../'], {relativeTo: activatedRoute});
    }

    /**
     * Realiza o cancel do cadastro e volta para a visualização quando o usuário estiver duplicando a entidade
     */
    cancelDuplicate(activatedRoute: ActivatedRoute) {
        this.router.navigate(['../../'], {relativeTo: activatedRoute});
    }


    /** Exibe uma nav-tab do formulário */
    showTabForm(idTab) {
        const tab = jQuery(`a[data-target='${idTab}']`) as any;
        tab.tab('show');
    }

    /** Verifica se o usuário logado é um representante e se a entidade informada não pertence a esse representante */
    isRepresentanteAndEntityNotRepresentante(entity: Representavel): boolean {
        if (!entity) {
            return false;
        }
        // Verifica se o usuário é um representante
        if (!this.authService.isRepresentante()) {
            return false;
        }
        // Verifica se a entidade informada não pertence ao representante do usuário logado
        return this.authService.getIdRepresentante() !== entity.idRepresentante;
    }

    /** Verifica se o usuário logado é um gerente/atendente e se o representante informado não pertence a esse gerente/atendente  */
    isGerenteOuAtendenteAndEntityNotSeuRepresentante(entity: Representante): boolean {
        if (!entity) {
            return false;
        }
        const idUsuario = this.authService.getIdUsuario();

        // Se for um gerente
        if (this.authService.isGerente()) {
            // Obtendo os ID's dos gerentes secundarios vinculados ao representante
            const idsGerentesSecundarios: number[] = [];
            if (entity.gerentes) {
                entity.gerentes.forEach(gerente => idsGerentesSecundarios.push(gerente.idUsuarioGerente));
            }

            // Se o id do gerente do representante é difirente do id do usuário logado (gerente) e também não estiver presente nos gerentes secundários, então a entidade não é do gerente
            return entity.idUsuarioGerente !== idUsuario && !idsGerentesSecundarios.includes(idUsuario);
        } else if (this.authService.isAtendente()) { // Se for um atendente
            // Obtendo os ID's dos atendentes secundarios vinculados ao representante
            const idsAtendentesSecundarios: number[] = [];
            if (entity.atendentes) {
                entity.atendentes.forEach(atendente => idsAtendentesSecundarios.push(atendente.idUsuarioAtendente));
            }

            // Se o id do atendente do representante é difirente do id do usuário logado (atendente) e também não estiver presente nos atendentes secundários, então a entidade não é do atendente
            return entity.idUsuarioAtendente !== idUsuario && !idsAtendentesSecundarios.includes(idUsuario);
        }

        return false;
    }

}
